import { FC, useState } from "react";
import { useTranslations } from "@jugl-web/utils";
import { InteractiveContainer, Tooltip } from "@jugl-web/ui-components";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { ReactComponent as StartDarkIcon } from "./assets/start-dark.svg";
import { ReactComponent as StartLightIcon } from "./assets/start-light.svg";
import { MessageCreditsDialog } from "./components/MessageCreditsDialog";
import { useSubscriptionV2 } from "../../providers/SubscriptionV2Provider";

interface CXCreditsTileProps {
  isExpanded?: boolean;
  theme?: "dark" | "light";
}

export const CXCreditsTile: FC<CXCreditsTileProps> = ({
  isExpanded = true,
  theme = "dark",
}) => {
  const { t } = useTranslations();
  const { locale } = Intl.DateTimeFormat().resolvedOptions();
  const { isSubscriptionCanceled, liteSubscription, planDefaultCredits } =
    useSubscriptionV2();
  const { isMember } = useUserRole();

  const [isMessageCreditsDialogOpen, setIsMessageCreditsDialogOpen] =
    useState(false);

  const aiCreditsQuota = liteSubscription?.quota?.["jugl::ai_credits"];
  const usage = aiCreditsQuota?.usage ?? 0;
  const allowed =
    (aiCreditsQuota?.allowed ?? 0) + (aiCreditsQuota?.balance ?? 0) ||
    planDefaultCredits;

  const isDark = theme === "dark";

  return (
    <>
      {!isExpanded ? (
        <Tooltip
          className="max-w-[310px] text-center"
          placement="right"
          isDisabled={isMember}
          renderTrigger={({ props, ref }) => (
            <InteractiveContainer
              ref={ref}
              className={
                isDark
                  ? "m-3 mx-auto flex h-10 w-[60px] items-center justify-center rounded-xl bg-[#2A2A2A]"
                  : "m-3 mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-primary-700"
              }
              {...props}
              isDisabled={isSubscriptionCanceled || isMember}
              onClick={() => setIsMessageCreditsDialogOpen(true)}
            >
              {isDark ? <StartDarkIcon /> : <StartLightIcon />}
            </InteractiveContainer>
          )}
        >
          {isSubscriptionCanceled
            ? t(
                {
                  id: "cx-sidebar.manage-canceled-subscription",
                  defaultMessage:
                    "Subscription is Canceled.{br}<underline>Manage Subscription</underline> to renew message credits usage",
                },
                {
                  br: <br />,
                  underline: (text) => (
                    <span className="underline">{text}</span>
                  ),
                }
              )
            : t({
                id: "cx-sidebar.credits-usage",
                defaultMessage: "Credits Usage",
              })}
        </Tooltip>
      ) : (
        <Tooltip
          className="max-w-[310px] text-center"
          isDisabled={!isSubscriptionCanceled || isMember}
          renderTrigger={({ props, ref }) => (
            <InteractiveContainer
              className={
                isDark
                  ? "m-2.5 space-y-3 rounded-xl bg-[#2A2A2A] px-2.5 py-4 text-white"
                  : "m-2.5 space-y-3 rounded-xl bg-primary-700 bg-gradient-to-b from-[#FBF6FF] to-[#F1F8FF] px-2.5 py-4 text-dark"
              }
              isDisabled={isSubscriptionCanceled || isMember}
              ref={ref}
              onClick={() => setIsMessageCreditsDialogOpen(true)}
              {...props}
            >
              <span
                className={
                  isDark
                    ? "font-secondary text-xs text-grey-300"
                    : "font-secondary text-xs text-dark-800"
                }
              >
                {t({
                  id: "cx-sidebar.message-credits-title",
                  defaultMessage: "Message credits",
                })}
              </span>
              <div className="flex items-center gap-2">
                {isDark ? (
                  <StartDarkIcon className="shrink-0" />
                ) : (
                  <StartLightIcon className="shrink-0" />
                )}
                <span className="font-secondary text-sm leading-[1] font-semibold">
                  {t(
                    {
                      id: "cx-sidebar.message-credits-used",
                      defaultMessage: "{used} of {total} Used",
                    },
                    {
                      used: new Intl.NumberFormat(locale).format(usage),
                      total: new Intl.NumberFormat(locale).format(allowed),
                    }
                  )}
                </span>
              </div>
            </InteractiveContainer>
          )}
        >
          {t(
            {
              id: "cx-sidebar.manage-canceled-subscription",
              defaultMessage:
                "Subscription is Canceled.{br}<underline>Manage Subscription</underline> to renew message credits usage",
            },
            {
              br: <br />,
              underline: (text) => <span className="underline">{text}</span>,
            }
          )}
        </Tooltip>
      )}
      <MessageCreditsDialog
        isOpen={isMessageCreditsDialogOpen}
        usage={usage}
        allowed={allowed}
        renewsDate={aiCreditsQuota?.end_date}
        onRequestClose={() => setIsMessageCreditsDialogOpen(false)}
      />
    </>
  );
};
